import { useCallback, useEffect, useState } from "react";
import { apiRequest, ApiError } from "../api/client";

const PAGE_SIZE = 20;

/**
 * Every list page (Users, Posts, Rooms, Withdrawals, ...) fetches its data
 * exactly the same way — a Range-header-paginated GET, tracked page state,
 * loading/error state. This hook is that pattern, written once. A page
 * component just supplies the path and any extra query params; everything
 * else (including "is there a next page" — inferred from getting a full
 * page back) is handled here.
 */
export function usePagedList<T>(path: string, params: Record<string, string | number | undefined> = {}) {
  const [rows, setRows] = useState<T[]>([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const paramsKey = JSON.stringify(params);

  const load = useCallback(() => {
    setIsLoading(true);
    setError(null);
    const start = page * PAGE_SIZE;
    const end = start + PAGE_SIZE - 1;
    apiRequest<T[]>(path, { params: { ...params, range: `${start}-${end}` } })
      .then(setRows)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "could not load data");
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, page, paramsKey, reloadToken]);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  return {
    rows,
    isLoading,
    error,
    page,
    setPage: (p: number) => setPage(Math.max(0, p)),
    hasNextPage: rows.length === PAGE_SIZE,
    pageSize: PAGE_SIZE,
    reload,
  };
}
